import React from 'react';

import { Chart, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartSeriesItemTooltip } from '@progress/kendo-react-charts';
import { Card, CardBody, CardTitle, Col, Container, Row } from 'react-bootstrap';
import { GenerateReport } from 'components/GenerateReport/GenerateReport';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixTable } from 'components/GMetrixTable/GMetrixTable';
import { ReportComponent, ReportSection } from 'apis/ReportApi';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSuperState } from 'hooks';

interface Report62Props {
    reportSections: ReportSection[]
}

export const Report62 = ({
    reportSections
}: Report62Props) => {

    const { formatMessage } = useIntl();

    const [state, setState] = useSuperState({
        detailsButtonReport: { scopeObjectId: 0, show: false, studentProfileId: 0, testName: '', testTypeId: 0 }
    });

    const { detailsButtonReport } = state;

    const donutCenterRenderer = (component: ReportComponent) => (
        <span>
            <h3>
                {component.CustomProperties.TestTotalValue}
            </h3>
            {' '}
            <FormattedMessage defaultMessage="Total Tests" id="total_tests.79a09c96" />
        </span>
    );

    const renderComponents = (component: ReportComponent, index: number) => {

        switch (component.ComponentName) {

            case 'HTMLContainer':
                return (
                    <Col
                        className="col-12 col-md-6 col-lg-3 my-1 my-mx-0"
                        key={`${component.ComponentId}-${index}`}
                    >
                        <Card className="h-100">
                            <CardBody>
                                <div className="d-flex align-items-center justify-content-end">
                                    <div className="col text-wrap">
                                        {component.Title}
                                    </div>
                                    <i
                                        className={component.CustomProperties.Icon}
                                        style={{ color: component.CustomProperties.FontColor ?? undefined, fontSize: '20px' }}
                                    ></i>
                                </div>
                                <CardTitle>
                                    <div>
                                        {component.CustomProperties.Value}
                                    </div>
                                </CardTitle>
                            </CardBody>
                        </Card>
                    </Col>
                );
            case 'Donut':
                return (
                    <Col
                        className="col-12 col-md-6 col-lg-6 my-1 my-mx-0 d-flex flex-column"
                        key={`${component.ComponentId}-${index}`}
                    >
                        <div className="text-dark bg-white border rounded-top p-3">
                            {component.Title}
                        </div>
                        <Chart
                            className="bg-white border border-top-0 rounded-bottom"
                            donutCenterRender={() => donutCenterRenderer(component)}
                            transitions={false}
                        >
                            <ChartSeries>
                                <ChartSeriesItem
                                    categoryField="TestType"
                                    data={component.CustomProperties.Value}
                                    field="Share"
                                    holeSize={80}
                                    type="donut"
                                >
                                    <ChartSeriesItemTooltip
                                        render={({ point }) => {

                                            const category = point?.category ?? '';
                                            const value = point?.value ?? 0;

                                            return (
                                                <div>
                                                    <FormattedMessage defaultMessage="Test Type" id="test_type.30d1f5ef" />
                                                    :
                                                    {category}
                                                    <br />
                                                    <FormattedMessage defaultMessage="Share" id="share.e11b4686" />
                                                    :
                                                    {value}
                                                    %
                                                </div>
                                            );

                                        }}
                                        visible={true}
                                    />
                                </ChartSeriesItem>
                            </ChartSeries>
                            <ChartLegend visible position="bottom" />
                        </Chart>
                    </Col>
                );
            case 'Bar':
                return (
                    <Col
                        className="col-12 col-md-6 col-lg-6 my-1 my-mx-0 d-flex flex-column"
                        key={`${component.ComponentId}-${index}`}
                    >
                        <div className="text-dark bg-white border rounded-top p-3">
                            {component.Title}
                        </div>
                        <Chart
                            className="bg-white border border-top-0 rounded-bottom"
                            style={{ padding: '2px' }}
                            transitions={false}
                        >
                            <ChartCategoryAxis>
                                <ChartCategoryAxisItem labels={{ visible: false }} />
                            </ChartCategoryAxis>
                            <ChartSeries>
                                <ChartSeriesItem
                                    categoryField="Test"
                                    color={'#007AC9'}
                                    data={component.CustomProperties.Value}
                                    field="Value"
                                    type="column"
                                >
                                    <ChartSeriesItemTooltip
                                        render={({ point }) => (
                                            <div>
                                                <FormattedMessage defaultMessage="Test" description={formatMessage({ id: 'gridview_header.884261d1', defaultMessage: 'Gridview header' })} id="test.051b8cd1" />
                                                :
                                                {point.category}
                                                <br />
                                                <FormattedMessage defaultMessage="Amount" id="amount.643c6c73" />
                                                :
                                                {point.value}
                                            </div>
                                        )}
                                        visible={true}
                                    />
                                </ChartSeriesItem>
                            </ChartSeries>
                        </Chart>
                    </Col>
                );
            case 'Table':
                if (component && component.Results && component.Results.Columns) {

                    const resultIdColumnIndex = component.Results.Columns.findIndex(col => col.field === 'ResultId');
                    const studentProfileIdColumnIndex = component.Results.Columns.findIndex(col => col.field === 'StudentProfileId');
                    const testTypeIdColumnIndex = component.Results.Columns.findIndex(col => col.field === 'TestTypeId');
                    component.Results.Columns[resultIdColumnIndex].dataType = 'Hidden';
                    component.Results.Columns[studentProfileIdColumnIndex].dataType = 'Hidden';
                    if (testTypeIdColumnIndex !== -1) component.Results.Columns[testTypeIdColumnIndex].dataType = 'Hidden';
                    return (
                        <GMetrixTable
                            allowExport
                            columnData={[...component.Results.Columns
                                .filter(col => col.dataType !== 'Hidden')
                                .map(col => col.field === 'Score'
                                    ? {
                                        ...col,
                                        cell: ({ dataItem }: any) => {

                                            const score = Number(dataItem.Score);
                                            const hasScore = dataItem.Score !== null && dataItem.Score !== undefined && dataItem.Score !== '' && !isNaN(score);

                                            return (
                                                <td>
                                                    {hasScore ? `${Math.floor(score / 10)}%` : ''}
                                                </td>
                                            );

                                        }
                                    }
                                    : col),
                            {
                                cell: ({ dataItem }: any) => (
                                    <td className="k-grid-content-sticky" style={{ background: 'white', left: 0, right: 0 }}>
                                        <div className="d-flex justify-content-end">
                                            <GMetrixButton
                                                onClick={async () => {

                                                    setState({ detailsButtonReport: { ...detailsButtonReport, scopeObjectId: dataItem.ResultId, show: true, studentProfileId: dataItem.StudentProfileId, testName: dataItem.Test, testTypeId: dataItem.TestTypeId } });

                                                }}
                                                onKeyDown={async e => {

                                                    if (e.key === 'Enter') {

                                                        setState({ detailsButtonReport: { ...detailsButtonReport, scopeObjectId: dataItem.ResultId, show: true, studentProfileId: dataItem.StudentProfileId, testName: dataItem.Test, testTypeId: dataItem.TestTypeId } });

                                                    }

                                                }}

                                                tabIndex={0}
                                                variant="primary"
                                            >
                                                <FormattedMessage defaultMessage="View" id="view.1590505b" />
                                            </GMetrixButton>
                                        </div>
                                    </td>
                                ),
                                locked: true,
                                width: '105px'
                            }]}
                            excelExportFileName={component.Title || 'Report62'}
                            exportContext="Report"
                            key={`${component.ComponentId}-${index}`}
                            lockColumns={true}
                            rowData={component.Results.Data}
                        />
                    );

                }

        }

    };

    // Set to true to render only the Table section and hide all other
    // components (HTMLContainer panes, Bar, Donut). Flip back to false to
    // restore the full report layout or just remove this part when the time comes.
    const showOnlyTable = true;

    return (
        <>
            {reportSections
                .filter(section => !showOnlyTable || section.Components.some(c => c.ComponentName === 'Table'))
                .map((section, reportSectionIndex) => (
                    <Container
                        fluid
                        className="mb-3 px-0"
                        key={reportSectionIndex}
                    >
                        <Row>
                            {section.Components
                                .filter(component => !showOnlyTable || component.ComponentName === 'Table')
                                .map((component, componentSectionIndex) => renderComponents(component, componentSectionIndex))}
                        </Row>
                    </Container>
                ))}

            <GMetrixDialog
                actionButtonClick={() => setState({ detailsButtonReport: { scopeObjectId: 0, show: false, studentProfileId: 0, testName: '', testTypeId: 0 } })}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'close.99e01fb5', defaultMessage: 'Close' })}
                dialogLabel={formatMessage({ id: 'single_test_attempt_report_testname.e77d0287', defaultMessage: 'Single Test Attempt Report - {testName}' }, { testName: detailsButtonReport.testName })}
                onCancel={() => setState({ detailsButtonReport: { scopeObjectId: 0, show: false, studentProfileId: 0, testName: '', testTypeId: 0 } })}
                show={detailsButtonReport.show}
                showCancelButton={false}
                size="xl"
            >
                {detailsButtonReport.show && (
                    <GenerateReport
                        showNoFilters
                        autoRunReport={true}
                        reportId={60}
                        scopeId={8}
                        scopeObjectId={detailsButtonReport.scopeObjectId}
                        studentProfileId={detailsButtonReport.studentProfileId}
                        testType={detailsButtonReport.testTypeId}
                    />
                )}
            </GMetrixDialog>
        </>
    );

};